import { exercise, number, section } from './helpers.ts';

export const reason = [
  exercise({
    id: 'reason-eggs',
    category: 'reason',
    title: 'Two test devices',
    topic: 'Worst-case reasoning',
    difficulty: 'Challenging',
    questions: [
      number(
        'eggs',
        'You have two identical test devices and a building with floors 1–100. There is an unknown highest safe floor T, which can be any integer from 0 to 100. A drop from floor T or below always leaves a device intact; a drop above T always breaks it. An intact device can be reused. What is the smallest number of drops that guarantees you can determine T, even in the worst case?',
        14,
        'drops',
        'Fourteen drops suffice: test floors 14, 27, 39, … with shrinking gaps. Thirteen drops can cover at most 13 + 12 + … + 1 = 91 floors.',
        ['decisions', 'capacity', 'budget', 'construction', 'proof'],
      ),
    ],
    reading: {
      title: 'Decision trees and worst-case search',
      sources: [],
      sections: [
        section(
          'decisions',
          'Strategies, decision trees, and guarantees',
          [
            {
              kind: 'paragraph',
              text: 'A search problem has a hidden answer and tests that reduce the remaining possibilities. A strategy says which test to make next after every possible sequence of results. It can be drawn as a decision tree: each branching point is a test, each branch a result, and each endpoint a final conclusion. The length of a path is the number of tests spent on that particular hidden answer.',
            },
            {
              kind: 'paragraph',
              text: 'The worst-case cost is the longest path. To guarantee a budget of k tests, every path must have length at most k. This differs from minimizing an average, which needs probabilities for the hidden answers. A method that usually finishes quickly but occasionally exceeds k is not a k-test guarantee.',
            },
            {
              kind: 'paragraph',
              text: 'For an ordinary yes/no search with no resource loss, splitting possibilities evenly often keeps the longest path short. With fragile testing equipment, the two results can leave different resources. A break leaves fewer devices, so two equally large groups of remaining floors need not be equally easy to resolve. The decision state must include both the remaining interval and the devices and drops still available.',
            },
          ],
          'Concept',
        ),
        section(
          'capacity',
          'Work backward from what the remaining budget can resolve',
          [
            {
              kind: 'example',
              title: 'Example · One device and three drops',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'With one device and three drops, a threshold over three floors can be found by testing floors 1, 2, and 3 in order. A break identifies the last safe floor immediately. Skipping floor 1 would be unsafe: if the only device broke at floor 2, thresholds 0 and 1 would still be indistinguishable. Thus one device and r drops can resolve r consecutive unknown floors, together with the possibility that none is safe.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Now take two devices and a budget of three drops. A first test at floor 3 is safe as a plan: if it breaks, two drops remain to resolve floors 1 and 2. If it survives, there are two drops left with both devices. The next gap can be two floors, then one. This covers 3 + 2 + 1 = 6 floors. Trying a first gap of four would fail on a first-drop break, so the gap limit is necessary as well as sufficient.',
            },
            {
              kind: 'paragraph',
              text: 'For a budget k, these shrinking capacities add to k + (k − 1) + … + 1. To calculate the sum, write it once forward and once backward. Each of the k paired positions sums to k + 1, so twice the total is k(k + 1). The total is k(k + 1)/2. We can now compare that capacity with the actual building size rather than guess test heights.',
            },
            {
              kind: 'equation',
              label: 'Two-device capacity with k drops',
              lines: [
                {
                  left: 'floors covered',
                  right: {
                    numerator: 'k(k + 1)',
                    denominator: '2',
                  },
                },
              ],
              note: 'The shrinking gaps are k, k − 1, …, 1; pairing the sum forward and backward derives the formula.',
            },
          ],
          'Method',
        ),
        section(
          'budget',
          'Why splitting in half is expensive',
          [
            {
              kind: 'paragraph',
              text: 'The experiment must identify T, the highest floor from which a device survives. T = 0 means even floor 1 breaks it; T = 100 means every floor is safe. A strategy needs to distinguish all 101 possibilities using only two devices. A broken device is unavailable for the rest of the experiment.',
            },
            {
              kind: 'paragraph',
              text: 'Binary search works when each test leaves you able to keep splitting. Here a broken device changes the experiment. With one device left, the only safe way to resolve an interval is to test its floors from bottom to top. If you skip a floor and the last device breaks, you cannot distinguish the skipped possibilities.',
            },
            {
              kind: 'paragraph',
              text: 'Suppose you promise to finish in k drops. Your first test must be no higher than floor k. If it breaks at floor k, you have used one drop and have k − 1 drops left to test floors 1 through k − 1. If none of those breaks the remaining device, the highest safe floor is k − 1; you do not need to test the already-known breaking floor again.',
            },
          ],
          'Daily application',
        ),
        section(
          'construction',
          'Let the gaps shrink',
          [
            {
              kind: 'paragraph',
              text: 'If the first device survives, your total budget has still fallen by one. The next gap can therefore be at most k − 1 floors, then k − 2, and so on. The test heights are cumulative sums, rather than equally spaced checkpoints.',
            },
            {
              kind: 'table',
              caption: 'A shrinking budget keeps the worst case bounded',
              headers: [
                'Checkpoint',
                'Drops used',
                'Possible floors below it',
                'Further drops at most',
              ],
              rows: [
                ['14', '1', '1–13', '13'],
                ['27', '2', '15–26', '12'],
                ['39', '3', '28–38', '11'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'For k = 14, use floors 14, 27, 39, 50, 60, 69, 77, 84, 90, 95, 99, and 100. Stop the sequence as soon as a device breaks. Starting just above the last safe checkpoint, use the second device to test upward one floor at a time. For example, a break at 39 comes on drop three. Floors 28 through 38 require at most eleven more drops, so the total is at most fourteen. If the checkpoint at 100 is safe, T is 100 and you are done.',
            },
          ],
          'Daily application',
        ),
        section(
          'proof',
          'Prove that fewer cannot work',
          [
            {
              kind: 'paragraph',
              text: 'A strategy is optimal only after we show that every smaller budget fails somewhere. With two devices and k drops, the largest possible number of floors covered is k + (k − 1) + … + 1 = k(k + 1)/2. The first gap cannot exceed k, because the first-break branch would be too long. Each surviving checkpoint reduces the remaining budget, which bounds the next gap in the same way.',
            },
            {
              kind: 'paragraph',
              text: 'Thirteen drops cover at most 91 floors, while fourteen cover 105. A 100-floor building therefore needs fourteen in the worst case. The extra capacity does not require inventing floors above the roof; we simply shorten the final gap.',
            },
            {
              kind: 'paragraph',
              text: 'Notice what was optimized: a guarantee, not the average number of drops. A prior belief that T is near the roof might suggest a different strategy for average performance. Nothing in this question provides such a probability distribution. This distinction between a worst-case promise and an average-case expectation is often the most consequential choice in planning an experiment.',
            },
          ],
          'Check',
        ),
      ],
    },
  }),
  exercise({
    id: 'reason-ants',
    category: 'reason',
    title: 'Ants on a stick',
    topic: 'Reasoning · Motion',
    difficulty: 'Moderate',
    questions: [
      number(
        'ants',
        'Five point-sized ants stand at positions 17, 31, 47, 68, and 83 cm on a straight 100 cm stick. Each walks at exactly 1 cm per second, initially either left or right. When two meet, both instantly reverse direction. An ant falls off upon reaching an end. Over all possible choices of initial directions, what is the latest possible time at which the last ant falls?',
        83,
        'seconds',
        'A collision is equivalent to two indistinguishable ants passing through one another. The longest straight trip from any starting position to an end is 83 cm.',
        ['models', 'extremes', 'exchange', 'bound', 'identity'],
      ),
    ],
    reading: {
      title: 'Equivalent models and bounds',
      sources: [],
      sections: [
        section(
          'models',
          'Simplify a model without changing the requested output',
          [
            {
              kind: 'paragraph',
              text: 'A model represents only the details needed for a question. Suppose identical tokens move along a line and we ask how many remain, not which named token occupies each point. Swapping two name tags changes the labeled description but not the occupied positions. A simplification is valid only if it preserves the quantity we intend to compute.',
            },
            {
              kind: 'paragraph',
              text: 'To justify a replacement rule, compare the systems immediately before and after the event being replaced. If they have the same relevant state afterward, they will give the same relevant behavior until the next event. Repeating that argument at each event extends a local equivalence to the entire motion. This is stronger than observing that a shortcut works in one drawing.',
            },
            {
              kind: 'paragraph',
              text: 'For constant-speed straight motion, time = distance / speed. The units explain the division: centimetres divided by centimetres per second leaves seconds. On a 10 cm line, a marker at 3 cm moving at 2 cm/s reaches the left end after 3/2 seconds or the right end after 7/2 seconds, depending on direction. Once interactions have been validly removed, each track can be analyzed this way.',
            },
            {
              kind: 'equation',
              label: 'Constant-speed travel',
              lines: [
                {
                  left: 'time',
                  right: {
                    numerator: 'distance',
                    denominator: 'speed',
                  },
                },
              ],
              note: 'Check units: cm ÷ (cm/s) = seconds.',
            },
          ],
          'Concept',
        ),
        section(
          'extremes',
          'A maximum needs a bound and an example',
          [
            {
              kind: 'paragraph',
              text: 'A claimed maximum M makes two assertions. First, every permitted configuration has value at most M: this is an upper bound. Second, at least one permitted configuration has value exactly M: the bound is attainable. Without the second assertion, M could simply be a loose estimate. For a minimum, the inequalities reverse.',
            },
            {
              kind: 'paragraph',
              text: 'For independent straight-moving markers, all have left by time M if every track ends by M. Choosing one starting direction that produces a track of length M then shows that departure cannot always finish sooner. In an interacting system, that argument only works after proving the straight-track representation preserves departure times. The ant solution below first establishes this equivalence, then supplies both halves of the maximum argument.',
            },
          ],
          'Method',
        ),
        section(
          'exchange',
          'Replace a collision with an exchange',
          [
            {
              kind: 'paragraph',
              text: 'The ants all move at the same speed on a 100 cm stick. We need the time when the stick becomes empty, regardless of which individual ant leaves last. Following every reversal separately is possible, but a different representation preserves exactly the departure times we need.',
            },
            {
              kind: 'paragraph',
              text: 'Imagine two ants approaching each other at equal speeds. Immediately after a collision there is one ant moving left and one moving right. The same occupied positions would result if the ants passed through each other without changing direction. The only difference would be which named ant occupied which position.',
            },
            {
              kind: 'table',
              caption: 'One collision, two descriptions',
              headers: ['Before meeting', 'After: real ants', 'After: pass-through tracks'],
              rows: [
                ['Left ant travels right', 'Left ant travels left', 'Rightward track continues'],
                ['Right ant travels left', 'Right ant travels right', 'Leftward track continues'],
                [
                  'Question asks exit times',
                  'Identities turn',
                  'Identities may be exchanged; occupied positions agree',
                ],
              ],
            },
            {
              kind: 'paragraph',
              text: 'One useful mental picture is to let the ants pass through while swapping name tags at every meeting. Their physical tracks then become straight lines. The tags follow the turning behavior of the real ants, but the set of occupied positions at every instant stays the same. Consequently the times at which ants leave the stick are unchanged. This argument depends on equal speeds and instantaneous reversals; it is not a rule for arbitrary collisions.',
            },
          ],
          'Daily application',
        ),
        section(
          'bound',
          'Find a limit and then reach it',
          [
            {
              kind: 'paragraph',
              text: 'In the pass-through picture, an ant starting at position x needs x seconds to reach the left end or 100 − x seconds to reach the right end. The possible travel times are therefore 17 or 83, 31 or 69, 47 or 53, 68 or 32, and 83 or 17 seconds. None exceeds 83. That proves an upper bound on the final departure time for every initial direction assignment.',
            },
            {
              kind: 'paragraph',
              text: 'An upper bound alone is not the answer: it might be impossible to achieve. Here it is achieved by making the ant at 17 initially head right, or the ant at 83 initially head left. Its corresponding straight-line track takes 83 seconds. Collisions may change which real ant completes that track, but cannot change when that track exits. For an especially direct example, point all ants right. No ant collides, and the one at 17 falls last, after 83 seconds.',
            },
          ],
          'Daily application',
        ),
        section(
          'identity',
          'Choose what the question needs you to preserve',
          [
            {
              kind: 'paragraph',
              text: 'Tracking individual ants is unnecessary when the question asks only when all ants have disappeared. It would matter if the question asked which named ant falls last. The simplified model preserves exit times but does not preserve personal identities along straight tracks.',
            },
            {
              kind: 'paragraph',
              text: 'This is a general problem-solving technique: identify the output you actually need, then discard distinctions that cannot change it. The simplification must be justified, rather than guessed from a pattern. Here we justified it locally at a collision and then applied the same replacement to every collision. We also checked both parts of the maximum claim: no arrangement can take longer, and at least one arrangement takes exactly that long. Many plausible puzzle solutions supply only the first half of this argument.',
            },
          ],
          'Check',
        ),
      ],
    },
  }),
  exercise({
    id: 'reason-bridge',
    category: 'reason',
    title: 'Bridge crossing',
    topic: 'Scheduling and optimization',
    difficulty: 'Challenging',
    questions: [
      number(
        'bridge',
        'Four people need 1, 3, 8, and 12 minutes respectively to cross a bridge. At most two may cross together, and a pair takes the slower person’s time. It is dark: every crossing in either direction requires the single torch, which must be carried and cannot be thrown. They all start on the same side. What is the minimum total time for everyone to reach the other side?',
        22,
        'minutes',
        'Send 1 and 3 (3), return 1 (1), send 8 and 12 (12), return 3 (3), send 1 and 3 (3): 22 minutes.',
        ['graphs', 'shortest-path', 'schedule', 'comparison', 'optimal'],
      ),
    ],
    reading: {
      title: 'States, schedules, and shortest paths',
      sources: [],
      sections: [
        section(
          'graphs',
          'Turn a schedule into states and legal moves',
          [
            {
              kind: 'paragraph',
              text: 'A scheduling problem is not just a list of task durations. Each action changes which later actions are possible. A state is a complete description of the information needed to determine those legal next actions. A route through states is a schedule, and its cost is the sum of the costs of its moves.',
            },
            {
              kind: 'example',
              title: 'Example · The cheapest first move can lose',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'A graph is a way to record this: nodes represent states and edges represent legal moves. A weighted edge carries a cost such as minutes. For a small example, suppose routes from S to G are S→A costing 2, A→G costing 8, S→B costing 4, and B→G costing 1. Choosing the cheapest first move goes to A and costs 10 overall. Going via B costs 5. Local cheapness does not establish a globally cheapest schedule.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: "In a bridge problem, a node must record everyone's bank and the torch's bank. Omitting the torch would identify states with different legal next moves. Four people have two bank choices each, giving 2 × 2 × 2 × 2 = 16 person arrangements. The torch doubles this to at most 32 states. Some cannot be reached legally, but the finite bound makes a systematic check possible.",
            },
          ],
          'Concept',
        ),
        section(
          'shortest-path',
          'Why checking the cheapest unfinished state works',
          [
            {
              kind: 'paragraph',
              text: 'For nonnegative move costs, a shortest-path method can find a minimum without guessing which schedule looks best. Give the start cost zero and other states an unknown cost. Repeatedly select the unprocessed state with the lowest known cost, examine every legal move from it, and update a neighbor if this route is cheaper than its previously known route. Mark the selected state processed. Store the preceding move as well if you want to recover the schedule.',
            },
            {
              kind: 'paragraph',
              text: 'In the small graph above, processing S discovers A at cost 2 and B at 4. Processing A finds G at 10. But G is not final yet: B at 4 must be processed next and improves G to 5. Only then is G the cheapest unprocessed state. This illustrates the difference between first discovering a route and proving it is cheapest.',
            },
            {
              kind: 'paragraph',
              text: "Why can a processed cost be trusted? Any competing path through an unprocessed state must already cost at least as much as the cheapest unprocessed state, and later nonnegative moves cannot reduce it. A cheaper path would therefore have had a cheaper frontier state available earlier. This is the core argument behind Dijkstra's algorithm. Negative move costs would invalidate it. Bridge times are positive, so the method can verify a promising human-designed schedule against every legal alternative.",
            },
          ],
          'Method',
        ),
        section(
          'schedule',
          'Use the fast walkers as a transport system',
          [
            {
              kind: 'paragraph',
              text: 'Call the four walkers A, B, C, and D, with crossing times 1, 3, 8, and 12 minutes. Everyone starts on the near bank with the single torch. A legal trip carries one or two people from the torch’s current bank to the other bank, and its duration is the slower traveller’s crossing time.',
            },
            {
              kind: 'paragraph',
              text: 'A forward crossing does not solve the scheduling problem if the torch is stranded on the far bank. The arrangement of people and the torch after each trip is the state you must track. Adding only the forward times will systematically underestimate the cost.',
            },
            {
              kind: 'paragraph',
              text: 'First send the 1-minute and 3-minute walkers across together, taking 3 minutes. The 1-minute walker returns, taking 1. Now the 8-minute and 12-minute walkers cross together, taking 12. The 3-minute walker, who was waiting on the far side, brings the torch back in 3. Finally the two fast walkers cross in 3. The total is 3 + 1 + 12 + 3 + 3 = 22 minutes. Check the state after each move: the returner must actually be on the bank with the torch.',
            },
            {
              kind: 'table',
              caption: 'Daily crossing schedule',
              headers: ['Trip', 'Travellers', 'Time', 'Total'],
              rows: [
                ['Across', '1 and 3', '3', '3'],
                ['Return', '1', '1', '4'],
                ['Across', '8 and 12', '12', '16'],
                ['Return', '3', '3', '19'],
                ['Across', '1 and 3', '3', '22'],
              ],
            },
          ],
          'Daily application',
        ),
        section(
          'comparison',
          'Compare the two ways of transporting the slow pair',
          [
            {
              kind: 'paragraph',
              text: 'For ordered crossing times a ≤ b ≤ c ≤ d, there are two standard ways to get the slowest two across while leaving the fastest available to help. One sends c and d together, using a and b to manage the torch. The complete four-person schedule costs a + 3b + d. Here that is 1 + 9 + 12 = 22.',
            },
            {
              kind: 'paragraph',
              text: 'The other uses a as the returner throughout: a with d, a back, a with c, a back, a with b. That costs 2a + b + c + d, or 2 + 3 + 8 + 12 = 25. “Always send the fastest back” sounds efficient locally, but makes us pay for the 8-minute crossing separately. Paying for a slightly slower return can save more on a later forward trip.',
            },
          ],
          'Daily application',
        ),
        section(
          'optimal',
          'How to establish the minimum',
          [
            {
              kind: 'paragraph',
              text: 'Comparing two promising schedules is not, by itself, proof that one is globally best. We can check the minimum by treating the puzzle as a shortest-path problem. A state records which bank each of the four walkers occupies and which bank holds the torch. There are at most 16 × 2 = 32 such states.',
            },
            {
              kind: 'paragraph',
              text: 'Start with cost zero for the state in which everyone is on the near bank. For each state reached, list every legal group of one or two walkers on the torch’s bank. Move that group and the torch to the other bank, adding the slower walker’s time. If this gives a cheaper route to the resulting state, keep the new cost and its preceding trip. Repeatedly examine the unexamined state with the lowest known cost.',
            },
            {
              kind: 'paragraph',
              text: 'Because every trip takes positive time, no route through a more expensive unexamined state can improve a state already examined this way. The first time the all-across state is examined, its cost is therefore the minimum over every legal schedule, including schedules with extra crossings. For times 1, 3, 8, and 12, that minimum is 22, and tracing its preceding trips recovers the five-trip schedule above. This exhaustive method provides the optimality check that a plausible schedule alone cannot supply.',
            },
          ],
          'Check',
        ),
      ],
    },
  }),
  exercise({
    id: 'reason-bottles',
    category: 'reason',
    title: 'Testing 1,000 bottles',
    topic: 'Reasoning · Experimental planning',
    difficulty: 'Challenging',
    questions: [
      number(
        'bottles',
        'Exactly one of 1,000 numbered bottles contains a contaminant. A test strip can receive a pooled sample from any chosen subset of bottles. After one hour, each strip reports positive if and only if its sample included the contaminated bottle. Tests are perfect, pooling does not dilute the signal, and you must choose every pool before seeing any result. What is the minimum number of strips that guarantees you can identify the bottle in one round?',
        10,
        'strips',
        'k binary results distinguish at most 2^k bottles. Nine distinguish 512, while ten distinguish 1,024. Assign each bottle a distinct 10-bit code and pool by bit position.',
        ['information', 'binary', 'patterns', 'encoding', 'assumptions'],
      ),
    ],
    reading: {
      title: 'Information and binary codes',
      sources: [],
      sections: [
        section(
          'information',
          'A measurement must distinguish the possible cases',
          [
            {
              kind: 'paragraph',
              text: 'Information is useful here when it separates hidden cases that would otherwise look the same. If two cases produce exactly the same observations, no interpretation of those observations can distinguish them with certainty. A successful experiment therefore assigns every candidate a different observable result pattern.',
            },
            {
              kind: 'paragraph',
              text: 'One yes/no measurement has two possible results. Two measurements have four patterns: no–no, no–yes, yes–no, yes–yes. Adding a third doubles the number to eight, because each old pattern can end in either no or yes. Repeating this reasoning gives 2^k patterns for k binary measurements; the exponent means multiplying k copies of 2. If there are more candidate cases than patterns, at least two cases must share a pattern. This is the counting principle behind a lower bound on the number of measurements.',
            },
            {
              kind: 'equation',
              label: 'Capacity of k binary measurements',
              lines: [
                {
                  left: 'distinct patterns',
                  right: '2ᵏ',
                },
              ],
              note: 'Each extra yes/no measurement doubles the number of possible result strings.',
            },
            {
              kind: 'paragraph',
              text: 'Enough patterns are necessary, but not sufficient: the physical experiment must actually be able to produce different ones for different candidates. That is why a complete solution also constructs the tests. Counting capacity alone is not an experimental design.',
            },
          ],
          'Concept',
        ),
        section(
          'binary',
          'Use binary codes to design measurements',
          [
            {
              kind: 'paragraph',
              text: 'A binary code is a fixed-length sequence of zeroes and ones. Think of each position as a question, with 1 meaning yes and 0 meaning no. Three positions can identify eight objects. For example, assign four objects the codes 000, 001, 010, and 011; test the objects marked 1 in each position. The observed pattern identifies which one was selected, if exactly one selected object is responsible for every positive result.',
            },
            {
              kind: 'paragraph',
              text: 'Binary numerals attach values 1, 2, 4, 8, and so on to these positions, from right to left. Each next value is twice the last. Thus 101 represents 4 + 1 = 5, and 110 represents 4 + 2 = 6. Leading zeroes keep codes the same length without changing their values. Uniqueness follows because each next place is larger than the sum of all smaller places: a difference in the highest differing place cannot be canceled by lower places.',
            },
            {
              kind: 'table',
              caption: 'Three binary positions encode a number',
              headers: ['Code', '4-place', '2-place', '1-place', 'Value'],
              rows: [
                ['000', '0', '0', '0', '0'],
                ['001', '0', '0', '1', '1'],
                ['101', '1', '0', '1', '5'],
                ['110', '1', '1', '0', '6'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'The code can be chosen before any results are known. This is different from an adaptive sequence in which the next question depends on the previous answer. When all tests must run in one round, code positions specify parallel measurements. The bottle construction below makes those positions into sample pools.',
            },
          ],
          'Method',
        ),
        section(
          'patterns',
          'Count distinguishable outcomes',
          [
            {
              kind: 'paragraph',
              text: 'The experiment has 1,000 possible hidden cases: each case names the one contaminated bottle. All pools must be chosen in advance, so the final pattern of positive and negative strips must identify the case without any follow-up tests.',
            },
            {
              kind: 'paragraph',
              text: 'A single perfect test strip has two possible results. With k strips, the entire result is a string of k yes-or-no values. There are 2^k such strings. If two candidate bottles always produce the same string, the experiment cannot tell them apart, regardless of how carefully we interpret the result.',
            },
            {
              kind: 'paragraph',
              text: 'The experiment therefore needs at least 1,000 distinct patterns. Nine strips supply only 512; ten supply 1,024. This is an information lower bound. It does not yet establish that useful pools can realize the patterns, so the second part of the solution is a construction.',
            },
          ],
          'Daily application',
        ),
        section(
          'encoding',
          'Build pools from binary addresses',
          [
            {
              kind: 'paragraph',
              text: 'Label the bottles with the integers 0 through 999 and write each label in binary using ten places, including leading zeroes. For each place, create one pool containing exactly the bottles that have a 1 in that place. A bottle can contribute to several pools. These memberships are selected before any strip is read.',
            },
            {
              kind: 'paragraph',
              text: 'Number the ten places by their values: 1, 2, 4, 8, 16, 32, 64, 128, 256, and 512. Bottle 13 belongs to pools 1, 4, and 8 because 13 = 1 + 4 + 8. If it is contaminated, precisely those strips become positive. Adding their place values recovers 13. In general, the complete pattern of strip results is exactly the contaminated bottle’s code. Every bottle has a different code, so ten strips suffice.',
            },
            {
              kind: 'paragraph',
              text: 'The all-zero code deserves attention. Bottle 0 contributes to no pool. If all strips are negative, we still identify it, because the prompt promises that exactly one bottle is contaminated. An untested bottle is not an ambiguity under that promise. If contamination could be absent, we would need a separate outcome for “none.” Ten strips would still suffice for 1,000 bottles plus that extra possibility, but we should assign nonzero codes to the bottles.',
            },
          ],
          'Daily application',
        ),
        section(
          'assumptions',
          'Understand the scope of the trick',
          [
            {
              kind: 'paragraph',
              text: 'The result depends on perfect binary tests, a single contaminated bottle, unlimited permitted pooling, and no dilution effect. If two bottles could be contaminated, the observed positive bits would be the union of their code bits; different pairs could produce the same union. The simple addressing method would then fail even though it still produces lots of result patterns.',
            },
            {
              kind: 'paragraph',
              text: 'This distinction is useful whenever a question asks for a minimum. Counting possible outcomes tells us what cannot work; a construction tells us what does work. Neither part substitutes for the other. Also note why sequential binary search is not an adequate explanation here: it would choose later pools after observing earlier results, which violates the one-round constraint. Binary encoding gets the same logarithmic scale while scheduling all measurements in advance.',
            },
          ],
          'Check',
        ),
      ],
    },
  }),
  exercise({
    id: 'reason-hats',
    category: 'reason',
    title: 'A line of hats',
    topic: 'Reasoning · Logic',
    difficulty: 'Challenging',
    questions: [
      number(
        'hats',
        'One hundred people stand in a line wearing hats that are either black or white. Each sees every hat ahead but not their own or any behind. Starting at the back and moving forward, each must say exactly one word, “black” or “white,” as their guess for their own hat. Everyone hears all earlier guesses. No other signals are allowed. They may agree on a deterministic strategy beforehand. What is the largest number of correct guesses they can guarantee for every hat arrangement?',
        99,
        'correct guesses',
        'The first person announces the parity of black hats ahead. Every later person recovers their own hat from that parity, visible hats, and already decoded hats. The first person cannot guarantee their own answer.',
        ['parity-basics', 'knowledge', 'parity', 'decoding', 'limit'],
      ),
    ],
    reading: {
      title: 'Parity and shared information',
      sources: [],
      sections: [
        section(
          'parity-basics',
          'What an even-or-odd check can tell you',
          [
            {
              kind: 'paragraph',
              text: 'Parity is the remainder after dividing an integer by two: even counts have remainder 0, odd counts remainder 1. Adding one flips parity, while adding two leaves it unchanged. We can encode white as 0 and black as 1; the parity of a collection then records whether its total number of black hats is even or odd.',
            },
            {
              kind: 'paragraph',
              text: 'Although one parity value does not reveal a whole collection, it can recover one missing value when all the others are known. Suppose three cards each show 0 or 1, and their total is known to be even. If two visible cards are 1 and 0, their sum is odd, so the hidden card must be 1 to make the full sum even. If the visible cards are 1 and 1, the hidden card must be 0. We are using a constraint to solve for one unknown, not guessing the missing card.',
            },
            {
              kind: 'table',
              caption: 'Recover one unknown from an even total',
              headers: ['Known cards', 'Known parity', 'Missing card', 'Check'],
              rows: [
                ['1, 0', 'Odd', '1', '1 + 0 + 1 = 2, even'],
                ['1, 1', 'Even', '0', '1 + 1 + 0 = 2, even'],
                ['0, 0', 'Even', '0', '0 + 0 + 0 = 0, even'],
              ],
            },
            {
              kind: 'paragraph',
              text: "For many cards the same rule works: compare the known part's parity with the announced total parity. If they match, the missing value is 0; otherwise it is 1. It is unnecessary to know the total count. An even-or-odd announcement contains exactly the distinction required for a single unknown binary value.",
            },
          ],
          'Concept',
        ),
        section(
          'knowledge',
          'Track what each person knows, then prove the guarantee',
          [
            {
              kind: 'paragraph',
              text: "A communication strategy can turn another person's observation into information you do not have. Its meaning must be agreed in advance: the word “black” could announce an odd count rather than report the speaker's own hat. A legal signal and a correct personal guess are different objectives, so using a guess as a signal can cost that speaker's guarantee.",
            },
            {
              kind: 'paragraph',
              text: 'To prove a sequence of deductions works, establish the first deduction and then show that every correct deduction leaves the next person enough information. This is an induction argument: a base case starts the chain and a repeatable step extends it. The hat strategy uses one shared parity constraint plus colors that become known as people speak. Each new person must be left with exactly one unknown within that constraint.',
            },
            {
              kind: 'paragraph',
              text: 'To prove a guarantee impossible, look for two hidden situations that give a decision-maker identical information but require different answers. Their strategy must choose the same answer in both, so at least one situation defeats it. This indistinguishability argument supplies an upper bound. It complements the constructive argument showing what the group can guarantee.',
            },
          ],
          'Method',
        ),
        section(
          'parity',
          'Use the first answer to establish a rule',
          [
            {
              kind: 'paragraph',
              text: 'The back person speaks first and can see the other 99 hats. The next person sees only 98, but also hears the first word. The group can use that word to convey information, even though the rules also count it as a guess about the first speaker’s own hat. The task is to guarantee correct guesses, not to maximize a success probability under random colours.',
            },
            {
              kind: 'paragraph',
              text: 'Parity records whether a count is even or odd. It ignores the magnitude of the count but detects a change of one. Before the hats are assigned, the group agrees that the first speaker will say “black” when the number of black hats ahead is odd, and “white” when it is even.',
            },
            {
              kind: 'paragraph',
              text: 'That word is officially the first speaker’s guess, so it may be wrong about their own hat. Its useful role is to transmit one bit describing the other ninety-nine hats. The remaining speakers all know the same parity constraint and can preserve it as the line progresses.',
            },
          ],
          'Daily application',
        ),
        section(
          'decoding',
          'Remove the hats you already know',
          [
            {
              kind: 'paragraph',
              text: 'The second speaker sees ninety-eight of the ninety-nine hats covered by the announced parity. If their visible count has the same parity as the announcement, their own hat must be white; adding a black hat would flip the parity. If the parities differ, their own hat must be black. They say the correct color, and everyone else learns that color from the answer.',
            },
            {
              kind: 'paragraph',
              text: 'The third speaker knows the second speaker’s hat and sees everyone ahead. That accounts for ninety-eight of the original ninety-nine hats again, leaving only their own unknown. Subtracting the known black hats from the shared parity tells them whether their own hat supplies the remaining odd contribution. Repeat this process down the line.',
            },
            {
              kind: 'paragraph',
              text: 'As a small example, suppose the announced count is even and the next speaker sees three black hats. Their own must be black, making the total four. The following speaker removes that newly known black hat before applying the same rule to the remaining hats. Nobody needs to know the first speaker’s actual hat: it was deliberately excluded from the transmitted count.',
            },
          ],
          'Daily application',
        ),
        section(
          'limit',
          'Why one hundred is impossible',
          [
            {
              kind: 'paragraph',
              text: 'Consider two arrangements that differ only in the first speaker’s own hat. They see exactly the same ninety-nine hats in both arrangements and have heard no earlier answers. A deterministic strategy therefore makes them say the same word in both. That word cannot be right for both possible colors of their own hat. No strategy can guarantee all one hundred answers.',
            },
            {
              kind: 'paragraph',
              text: 'The parity strategy guarantees the remaining ninety-nine, so the upper bound is achieved. This is not an average-case assertion that someone is “probably” right; it works for every arrangement. The first speaker may happen to be right, giving 100 correct guesses in some arrangements. But their own hat can be changed without changing anything they observe, so 100 is never a guarantee. The distinction between a possible outcome and an assured outcome is the reason the answer is 99.',
            },
          ],
          'Check',
        ),
      ],
    },
  }),
];
